/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/
import { useRef } from "react";

import * as React from "react";
import { OverlayTrigger } from "@spectrum-web-components/overlay";
import { useEventListener } from "hooks/useEventListener";
import styled from "styled-components";
import { useIsMobile } from "components/shared/Styles";
import type { Placement } from "@spectrum-web-components/overlay";

interface TooltipProps {
  open: boolean;
  message: string;
  onClose: VoidFunction;
  className?: string;
}

/*
Thit is a helper component for working with SWC tooltips that are
triggered programmatically rather than through default interactions. SWC tooltips
rely on overlay triggers to hide/show the content. Overlay triggers require a
trigger component + interaction type, and while they allow the overlay to be
shown programmatically, doing so doesn't prevent it from being shown via the
interaction trigger.

This sets up a hidden element that has the same position + dimensions as the children
this component wraps to serve as the overlay trigger. All pointer events are disabled
on the hidden trigger so that the tooltip can be trigger exclusively programmatically.
 */
export const ManualTooltip: React.FC<TooltipProps> = ({
  children,
  message,
  open,
  onClose,
  className,
}) => {
  const overlayRef = useRef<OverlayTrigger | null>(null);

  useEventListener(
    "sp-closed",
    () => {
      onClose?.();
    },
    overlayRef.current
  );

  return (
    <TooltipContext className={className}>
      {children}
      <TooltipContainer>
        <overlay-trigger
          placement="bottom"
          open={open ? "hover" : undefined}
          ref={overlayRef}
        >
          <TooltipTrigger slot="trigger" />
          <SWCTooltip placement="bottom" slot="hover-content" open>
            {message}
          </SWCTooltip>
        </overlay-trigger>
      </TooltipContainer>
    </TooltipContext>
  );
};

interface SWCTooltipProps {
  placement?: Placement;
  slot?: string;
  open: boolean;
}

export const SWCTooltip: React.FC<SWCTooltipProps> = ({
  children,
  placement = "left",
  slot,
  open,
}) => {
  const isMobile = useIsMobile();

  return (
    <sp-tooltip
      placement={placement}
      slot={isMobile ? "click-content" : slot}
      open={open}
    >
      {children}
    </sp-tooltip>
  );
};

const TooltipContext = styled.div`
  position: relative;
`;

const TooltipContainer = styled.div`
  position: absolute;
  pointer-events: none;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const TooltipTrigger = styled.div`
  width: 100%;
  height: 100%;
  opacity: 0;
`;
